import { useFlags } from 'launchdarkly-react-client-sdk';
import { showsAdminExperience, useHasAdminAccess } from 'eds-common/api/v2';

export interface AdminExperience {
  /** Whether the /data section should be rendered as the admin experience. */
  showsAdminExperience: boolean;
  /** True until the answer is settled — no branch should render yet. */
  isLoading: boolean;
}

/**
 * Whether this user gets the admin /data experience.
 *
 * The flag applies only to users who are admins by permission. They still have
 * a full teacher experience to fall back on — their own classes and reports —
 * so until the admin experience is switched on they keep it, rather than
 * losing a working section to a placeholder.
 *
 * An administrator by role has no teacher experience to fall back to, so they
 * stay on the admin branch regardless; what the flag controls for them is
 * whether that branch shows the reports or the Coming Soon page.
 */
export function useAdminExperience(): AdminExperience {
  const { isAdministrator, hasExtendedPermission, isLoading } =
    useHasAdminAccess();
  const { dndShowAdminReport } = useFlags();

  return {
    showsAdminExperience: showsAdminExperience({
      isAdministrator,
      hasExtendedPermission,
      showAdminReportFlag: !!dndShowAdminReport,
    }),
    isLoading,
  };
}
